import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useI18n } from '@/contexts/I18nContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { StatusBadge } from '@/components/StatusBadge';
import { FileText, Upload, IdCard, Bell, ArrowRight, Plus } from 'lucide-react';
import api from '@/lib/api';

export default function CitizenDashboard() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [apps, setApps] = useState([]);
  const [notifCount, setNotifCount] = useState(0);
  const [cards, setCards] = useState([]);

  useEffect(() => {
    api.get('/applications/my').then(r => setApps(r.data)).catch(() => {});
    api.get('/notifications/unread-count').then(r => setNotifCount(r.data.count)).catch(() => {});
    api.get('/cards/my').then(r => setCards(r.data)).catch(() => {});
  }, []);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="citizen-dashboard">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
          {t('dash.welcome')} {user?.name}
        </h1>
        <p className="text-muted-foreground mt-1">{t('dash.manage')}</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-10 h-10 rounded-lg bg-[#007FFF]/10 flex items-center justify-center">
              <FileText className="w-5 h-5 text-[#007FFF]" />
            </div>
            <div>
              <p className="text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>{apps.length}</p>
              <p className="text-xs text-muted-foreground">{t('dash.applications')}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-10 h-10 rounded-lg bg-[#009543]/10 flex items-center justify-center">
              <IdCard className="w-5 h-5 text-[#009543]" />
            </div>
            <div>
              <p className="text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>{cards.length}</p>
              <p className="text-xs text-muted-foreground">{t('dash.cards')}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-10 h-10 rounded-lg bg-[#F7D618]/10 flex items-center justify-center">
              <Upload className="w-5 h-5 text-[#D4A800]" />
            </div>
            <div>
              <p className="text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {apps.filter(a => a.status === 'submitted').length}
              </p>
              <p className="text-xs text-muted-foreground">{t('dash.pending')}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-10 h-10 rounded-lg bg-[#CE1021]/10 flex items-center justify-center">
              <Bell className="w-5 h-5 text-[#CE1021]" />
            </div>
            <div>
              <p className="text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>{notifCount}</p>
              <p className="text-xs text-muted-foreground">{t('citizen.notifications')}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {apps.length === 0 ? (
        <Card className="border">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 rounded-2xl bg-[#007FFF]/10 flex items-center justify-center mx-auto mb-4">
              <FileText className="w-8 h-8 text-[#007FFF]" />
            </div>
            <h3 className="text-lg font-semibold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>{t('dash.no_app')}</h3>
            <p className="text-sm text-muted-foreground mb-4">{t('dash.no_app_desc')}</p>
            <Link to="/citizen/new-application">
              <Button className="bg-[#007FFF] hover:bg-[#0066CC]" data-testid="create-first-app-btn">
                <Plus className="w-4 h-4 mr-2" /> {t('dash.create')}
              </Button>
            </Link>
          </CardContent>
        </Card>
      ) : (
        <Card className="border">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>{t('dash.my_apps')}</CardTitle>
              <Link to="/citizen/new-application">
                <Button size="sm" className="bg-[#007FFF] hover:bg-[#0066CC]" data-testid="create-new-app-btn">
                  <Plus className="w-4 h-4 mr-1" /> {t('dash.new')}
                </Button>
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {apps.slice(0, 5).map(app => (
                <div key={app.application_id} className="flex items-center justify-between p-3 rounded-lg border border-border hover:bg-muted/50 transition-colors">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{app.personal_info?.full_name || 'Sans nom'}</p>
                    <p className="text-xs text-muted-foreground">{app.personal_info?.country_of_residence}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <StatusBadge status={app.status} />
                    <Link to="/citizen/track-status">
                      <ArrowRight className="w-4 h-4 text-muted-foreground" />
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
